/**
 * LayoutView.jsx — CASCA real do Agendário (Tarefa B). Substitui o ShellScaffold.
 * Pura: recebe { vm, actions, children }. Estiliza só via `sx` + CSS vars (var(--*)).
 *
 * Contrato: shell/layout.vm.js (@version 6)
 *   vm.nav[]      → { key, label, iconName, route, migrated }
 *   vm.header     → { appTitle, appSubtitle, userName, userInitials, notificationsCount,
 *                     whatsapp: { visible, status, creditsLabel, tooltip }, colorMode }
 *   vm.activeRoute
 *   actions       → onNavigate, onLogout, onOpenNotifications, onOpenSettings,
 *                   onOpenSupportWidget, onOpenWhatsApp, onToggleColorMode
 *   headerExtras  → (slot opcional) nó renderizado no lugar do sino embutido — o app real injeta
 *                   o NotificationCenter completo aqui; sem ele, o sino embutido renderiza (DP).
 * Estado UI-local (permitido): drawer mobile aberto/fechado.
 */
import { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Box, Typography, Avatar, IconButton, Badge, Stack, Tooltip, CircularProgress,
} from '@mui/material';
import WhatsApp from '@mui/icons-material/WhatsApp';
import MenuRounded from '@mui/icons-material/Menu';
import Settings from '@mui/icons-material/Settings';
import MonitorHeart from '@mui/icons-material/MonitorHeart';
import WorkspacePremium from '@mui/icons-material/WorkspacePremium';
import Dashboard from '@mui/icons-material/Dashboard';
import EventNote from '@mui/icons-material/EventNote';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import Person from '@mui/icons-material/Person';
import People from '@mui/icons-material/People';
import Business from '@mui/icons-material/Business';
import ContentCut from '@mui/icons-material/ContentCut';
import Payment from '@mui/icons-material/Payment';
import EventBusy from '@mui/icons-material/EventBusy';
import WebAsset from '@mui/icons-material/WebAsset';
import AccountBalanceWallet from '@mui/icons-material/AccountBalanceWallet';
import Assessment from '@mui/icons-material/Assessment';
import Share from '@mui/icons-material/Share';
import Hub from '@mui/icons-material/Hub';
import SupportAgent from '@mui/icons-material/SupportAgent';
import Notifications from '@mui/icons-material/Notifications';
import LightMode from '@mui/icons-material/LightModeOutlined';
import DarkMode from '@mui/icons-material/DarkModeOutlined';
import Logout from '@mui/icons-material/Logout';
import HelpOutline from '@mui/icons-material/HelpOutline';
// Ícones TEMÁTICOS usados pelos temas por segmento (themes/templates/*.json → icons.*.name).
// Tema novo com ícone fora desta lista cai no HelpOutline — registrar aqui (checklist §6b).
import EventAvailable from '@mui/icons-material/EventAvailable';
import Schedule from '@mui/icons-material/Schedule';
import CalendarToday from '@mui/icons-material/CalendarToday';
import Event from '@mui/icons-material/Event';
import HomeWork from '@mui/icons-material/HomeWork';
import Storefront from '@mui/icons-material/Storefront';
import Store from '@mui/icons-material/Store';
import MeetingRoom from '@mui/icons-material/MeetingRoom';
import Weekend from '@mui/icons-material/Weekend';
import LocalHospital from '@mui/icons-material/LocalHospital';
import Face from '@mui/icons-material/Face';
import Group from '@mui/icons-material/Group';
import Groups from '@mui/icons-material/Groups';
import Woman from '@mui/icons-material/Woman';
import PersonSearch from '@mui/icons-material/PersonSearch';
import SelfImprovement from '@mui/icons-material/SelfImprovement';
import BeachAccess from '@mui/icons-material/BeachAccess';
import NaturePeople from '@mui/icons-material/NaturePeople';
import Block from '@mui/icons-material/Block';
import Spa from '@mui/icons-material/Spa';
import MedicalServices from '@mui/icons-material/MedicalServices';
import MiscellaneousServices from '@mui/icons-material/MiscellaneousServices';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import FitnessCenter from '@mui/icons-material/FitnessCenter';
import SportsGymnastics from '@mui/icons-material/SportsGymnastics';
import DirectionsRun from '@mui/icons-material/DirectionsRun';
import Psychology from '@mui/icons-material/Psychology';
import Brush from '@mui/icons-material/Brush';
import ColorLens from '@mui/icons-material/ColorLens';
import School from '@mui/icons-material/School';
import MenuBook from '@mui/icons-material/MenuBook';
import DesignServices from '@mui/icons-material/DesignServices';
import Forum from '@mui/icons-material/Forum';
import BadgeIcon from '@mui/icons-material/Badge';

const ICONS = {
  Dashboard, EventNote, CalendarMonth, Person, People, Business, ContentCut,
  Payment, EventBusy, WebAsset, AccountBalanceWallet, Assessment, Share, Hub, SupportAgent,
  MonitorHeart, WorkspacePremium,
  // temáticos (por segmento)
  EventAvailable, Schedule, CalendarToday, Event, HomeWork, Storefront, Store, MeetingRoom,
  Weekend, LocalHospital, Face, Group, Groups, Woman, PersonSearch, SelfImprovement, BeachAccess,
  NaturePeople, Block, Spa, MedicalServices, MiscellaneousServices, FavoriteBorder, FitnessCenter,
  SportsGymnastics, DirectionsRun, Psychology, Brush, ColorLens, School, MenuBook,
  DesignServices, Forum, Badge: BadgeIcon,
};
const DRAWER_W = 276;

/* ── Paleta do MENU LATERAL ─────────────────────────────────────────────────
 * Todo texto/ícone/linha DENTRO do drawer usa --text-drawer (não a cor global),
 * para que temas de drawer ESCURO com texto global ESCURO continuem legíveis.
 * Fallback: se o tema não definir --text-drawer, cai no texto global — temas
 * de drawer claro ficam idênticos ao comportamento atual.
 * Variações (secundário/linhas) derivam por color-mix — nenhum token novo.
 */
const DRAWER_TEXT = 'var(--text-drawer, var(--text-primary))';
// Cor de MARCA legível SOBRE o drawer (rótulo/ícone do item ativo + barra de acento).
// --color-primary é cor de PREENCHIMENTO (precisa ~6:1 com --on-primary em botão/badge), então
// em drawer escuro ela some: framboesa #B81E63 sobre ameixa #1F1420 = 2.59:1. Temas de drawer
// escuro definem --drawer-accent (tom claro da marca); os demais caem na primária.
// ⚠️ VAR NOVA a registrar no schema: --drawer-accent.
const DRAWER_ACCENT = 'var(--drawer-accent, var(--color-primary))';
const DRAWER_TEXT_MUTED = `color-mix(in srgb, ${DRAWER_TEXT} 62%, transparent)`;
const DRAWER_LINE = `color-mix(in srgb, ${DRAWER_TEXT} 16%, transparent)`;

/* ── Texto sobre preenchimento de ESTADO (badge de notificação, pílula de saldo) ─────────────
 * Era '#fff' cravado. No modo escuro --color-error é luminoso (#F76F61) e branco em cima dele
 * reprova; --surface resolve os dois modos com UM token: no claro a superfície é branca sobre
 * um vermelho profundo, no escuro é carbono sobre um vermelho claro. Sempre ≥4,5:1.
 */
const ON_STATE_FILL = 'var(--surface)';

/**
 * Logo oficial Agendário.
 * URL interna genérica (Vite: arquivo em /public/brand/agendario-logo.png).
 * Quando a hospedagem definitiva existir (CDN), basta trocar esta constante.
 *
 * NOTA DE RECORTE (arte atual = PNG 640×640, fundo BRANCO, com respiro ao redor do "A"):
 *  - Header usa o arquivo cheio; object-fit: cover recorta o respiro e centraliza — nenhum recorte manual obrigatório.
 *  - Recortes desejáveis quando possível:
 *      1. Versão com fundo TRANSPARENTE (para uso direto sobre superfícies escuras/temas).
 *      2. Favicon: 32×32 e 180×180 (apple-touch), recortada justa no símbolo.
 */
const BRAND_LOGO_URL = '/brand/agendario-logo.png';
// Fundo do tile do logo. Único branco literal que sobrou na casca, e sobrou porque a ARTE tem
// fundo branco: trocar por var(--surface) no escuro criaria uma moldura carbono ao redor de um
// quadrado branco. Com o PNG de fundo transparente (pedido na nota acima), isso vira 'transparent'.
const BRAND_LOGO_TILE = '#FFFFFF';

export default function LayoutView({ vm, actions, children, headerExtras }) {
  const { nav = [], header = {}, activeRoute } = vm || {};
  const [mobileOpen, setMobileOpen] = useState(false); // UI-local

  const drawerContent = (
    <DrawerContent nav={nav} activeRoute={activeRoute} actions={actions}
      onNavigateDone={() => setMobileOpen(false)} />
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'var(--bg-page)' }}>
      {/* ---------------- Header ---------------- */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: 'var(--surface)',
          color: 'var(--text-primary)',
          borderBottom: '1px solid var(--divider)',
        }}
      >
        <Toolbar sx={{ gap: { xs: .5, md: 1.5 }, minHeight: 64, px: { xs: 1, sm: 2 } }}>
          {/* hamburger (mobile) */}
          <IconButton aria-label="Abrir menu" onClick={() => setMobileOpen(true)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' }, width: 44, height: 44,
              color: 'var(--text-primary)', borderRadius: 'var(--radius-sm)',
              '&:hover': { bgcolor: 'var(--color-primary-soft)' },
              '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '-2px' },
            }}>
            <MenuRounded />
          </IconButton>

          {/* marca — alinhada à largura do drawer no desktop; compacta no mobile */}
          <Stack direction="row" spacing={1.25} alignItems="center"
            sx={{ width: { xs: 'auto', md: DRAWER_W - 24 }, pr: { xs: 1, md: 2 }, minWidth: 0 }}>
            {/* Logo oficial — a arte tem FUNDO BRANCO chapado, então o tile é branco nos dois modos:
                não é "fundo claro cravado" por descuido, é o recorte da arte (ver NOTA DE RECORTE
                acima — com uma versão de fundo transparente isso passa a ser var(--surface)).
                object-fit: cover preenche o tile e recorta o respiro da arte, centralizado. */}
            <Box aria-hidden sx={{
              width: 38, height: 38, flexShrink: 0, borderRadius: 'var(--radius-sm)',
              bgcolor: BRAND_LOGO_TILE, border: '1px solid var(--divider)',
              overflow: 'hidden',
              boxShadow: 'var(--shadow-card)',
            }}>
              <Box component="img" src={BRAND_LOGO_URL} alt=""
                sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontWeight: 700, fontSize: 17, lineHeight: 1.1, letterSpacing: '.2px', color: 'var(--text-primary)' }}>
                {header.appTitle}
              </Typography>
              <Typography noWrap variant="caption" sx={{ color: 'var(--text-secondary)', display: { xs: 'none', sm: 'block' } }}>
                {header.appSubtitle}
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ flex: 1 }} />

          {/* WhatsApp: canal + saldo de créditos (layout.vm @v5) */}
          <HeaderWhatsAppChip wa={header.whatsapp} onOpen={() => actions?.onOpenWhatsApp?.()} />

          {/* Modo claro/escuro (layout.vm @v6) */}
          <ModeToggle mode={header.colorMode} onToggle={() => actions?.onToggleColorMode?.()} />

          {/* cluster de ações: notificações + configurações */}
          <Stack direction="row" spacing={.25} alignItems="center"
            sx={{
              px: .5, py: .25,
              borderRadius: 999, border: '1px solid var(--divider)', bgcolor: 'var(--bg-page)',
            }}>
            {headerExtras || (
              <Tooltip title="Notificações">
                <IconButton aria-label={`Notificações${header.notificationsCount ? `: ${header.notificationsCount} novas` : ''}`}
                  onClick={() => actions?.onOpenNotifications?.()}
                  sx={{
                    width: 40, height: 40, color: 'var(--text-secondary)',
                    '&:hover': { color: 'var(--text-primary)', bgcolor: 'var(--color-primary-soft)' },
                    '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '-2px' },
                  }}>
                  <Badge
                    badgeContent={header.notificationsCount}
                    overlap="circular"
                    sx={{ '& .MuiBadge-badge': { bgcolor: 'var(--color-error)', color: ON_STATE_FILL, fontWeight: 700 } }}
                  >
                    <Notifications sx={{ fontSize: 22 }} />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Configurações da conta">
              <IconButton aria-label="Configurações da conta" onClick={() => actions?.onOpenSettings?.()}
                sx={{
                  width: 40, height: 40, color: 'var(--text-secondary)',
                  '&:hover': { color: 'var(--text-primary)', bgcolor: 'var(--color-primary-soft)' },
                  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '-2px' },
                }}>
                <Settings sx={{ fontSize: 22 }} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Box sx={{ width: '1px', height: 28, bgcolor: 'var(--divider)', mx: { xs: .5, sm: 1 }, display: { xs: 'none', sm: 'block' } }} />

          {/* usuário + sair */}
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            <Tooltip title="Configurações da conta">
              <Stack direction="row" spacing={1.25} alignItems="center"
                role="button" tabIndex={0} aria-label="Abrir configurações da conta"
                onClick={() => actions?.onOpenSettings?.()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); actions?.onOpenSettings?.(); }
                }}
                sx={{
                  minWidth: 0, cursor: 'pointer', borderRadius: 'var(--radius-sm)', mx: -.75, px: .75, py: .25,
                  '&:hover': { bgcolor: 'var(--color-primary-soft)' },
                  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '-2px' },
                }}>
                <Avatar sx={{
                  width: 36, height: 36, flexShrink: 0, fontWeight: 700, fontSize: 15,
                  bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary)',
                  border: '1px solid var(--color-primary-light)',
                }}>
                  {header.userInitials}
                </Avatar>
                <Box sx={{ display: { xs: 'none', md: 'block' }, lineHeight: 1.15, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{header.userName}</Typography>
                  <Typography noWrap variant="caption" sx={{ color: 'var(--text-secondary)' }}>Minha conta</Typography>
                </Box>
              </Stack>
            </Tooltip>
            <Tooltip title="Sair">
              <IconButton aria-label="Sair" onClick={() => actions?.onLogout?.()}
                sx={{
                  width: 40, height: 40, color: 'var(--text-secondary)',
                  '&:hover': { color: 'var(--color-error)', bgcolor: 'var(--tint-error-bg)' },
                  '&:focus-visible': { outline: '2px solid var(--color-error)', outlineOffset: '-2px' },
                }}>
                <Logout sx={{ fontSize: 22 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* ---------------- Sidebar (temporário no mobile, permanente no desktop) ---------------- */}
      <Box component="nav" sx={{ width: { md: DRAWER_W }, flexShrink: { md: 0 } }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_W, boxSizing: 'border-box',
            bgcolor: 'var(--bg-drawer)', borderRight: '1px solid var(--divider)',
            color: DRAWER_TEXT, display: 'flex', flexDirection: 'column',
          },
        }}
      >
        {drawerContent}
      </Drawer>
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: DRAWER_W, boxSizing: 'border-box',
            bgcolor: 'var(--bg-drawer)', borderRight: '1px solid var(--divider)',
            color: DRAWER_TEXT, display: 'flex', flexDirection: 'column',
          },
        }}
      >
        {drawerContent}
      </Drawer>
      </Box>

      {/* ---------------- Conteúdo ---------------- */}
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Toolbar />
        <Box sx={{ p: { xs: 2, md: 3 }, flex: 1 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

/* ── Modo claro/escuro (layout.vm @v6) ──────────────────────────────────────────
 * SEGMENTADO de dois estados, não interruptor. O dono do negócio abre o painel no modo que o
 * nicho definiu (Barbearia e Tatuagem no escuro) e pode nunca mais tocar aqui — então o controle
 * tem que responder "em que modo eu estou?" antes de oferecer a troca. Um switch responde isso
 * só por posição; um segmentado responde por TRÊS sinais somados, nenhum deles cor:
 *   1. o segmento ativo ganha a superfície (--surface) recortada do trilho (--bg-page) + o fio
 *      assentado de --shadow-card — lido como botão pressionado nos dois modos;
 *   2. só o ativo mostra o RÓTULO ("Claro"/"Escuro") ao lado do ícone, em lg+;
 *   3. aria-pressed + aria-label com "(ativo)", e o tooltip diz o estado E o que o clique faz.
 * A View não decide nada: clicar no segmento inativo só dispara onToggleColorMode().
 * Alvo de toque: 44×40 por segmento.
 */
const MODE_OPTS = [
  { key: 'light', label: 'Claro', Icon: LightMode },
  { key: 'dark', label: 'Escuro', Icon: DarkMode },
];

function ModeToggle({ mode, onToggle }) {
  const current = mode === 'dark' ? 'dark' : 'light';
  const tip = current === 'dark'
    ? 'Modo escuro ativo — clique para o claro'
    : 'Modo claro ativo — clique para o escuro';

  return (
    <Tooltip title={tip}>
      <Stack direction="row" role="group" aria-label="Modo de exibição do painel"
        sx={{
          p: '2px', flexShrink: 0, alignItems: 'center',
          borderRadius: 999, border: '1px solid var(--divider)', bgcolor: 'var(--bg-page)',
        }}>
        {MODE_OPTS.map((opt) => {
          const { key, label } = opt;
          const ModeIcon = opt.Icon;
          const on = current === key;
          return (
            <Box key={key} component="button" type="button"
              aria-pressed={on}
              aria-label={`Modo ${label.toLowerCase()}${on ? ' (ativo)' : ''}`}
              onClick={() => { if (!on) onToggle?.(); }}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .625,
                minWidth: 44, height: 40, px: on ? 1.25 : 1, border: 0, cursor: on ? 'default' : 'pointer',
                borderRadius: 999, font: 'inherit',
                bgcolor: on ? 'var(--surface)' : 'transparent',
                color: on ? 'var(--text-primary)' : 'var(--text-secondary)',
                boxShadow: on ? 'var(--shadow-card)' : 'none',
                transition: 'background-color .18s ease, color .18s ease, box-shadow .18s ease',
                '&:hover': on ? {} : { color: 'var(--text-primary)', bgcolor: 'var(--color-primary-soft)' },
                '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '-2px' },
              }}>
              <ModeIcon aria-hidden sx={{ fontSize: 19 }} />
              {on && (
                <Typography component="span" variant="body2"
                  sx={{ display: { xs: 'none', lg: 'block' }, fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap' }}>
                  {label}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    </Tooltip>
  );
}

/* ── Chip WhatsApp do header ──────────────────────────────────────────────────
 * Responde de relance: "o canal está funcionando e quanto saldo eu tenho?".
 * Estados (vm.header.whatsapp.status): ok | low | depleted | losing | loading | disabled.
 * Estado NUNCA só por cor: cada um tem texto próprio no chip (desktop) e no
 * aria-label/tooltip (que vem formatado do VM). Clique → onOpenWhatsApp
 * (tela de Integrações). visible:false → não renderiza nada (kill-switch).
 * Desktop = pill com saldo; mobile (xs–sm) = só ícone + badge numérica.
 */
const WA_STYLES = {
  ok: {
    border: 'var(--divider)', bg: 'var(--bg-page)',
    icon: 'var(--wa-action)', fg: 'var(--text-primary)', sub: null,
  },
  low: {
    border: 'var(--tint-warning-border)', bg: 'var(--tint-warning-bg)',
    icon: 'var(--tint-warning-fg)', fg: 'var(--tint-warning-fg)', sub: 'Pouco saldo',
  },
  depleted: {
    border: 'var(--tint-error-border)', bg: 'var(--tint-error-bg)',
    icon: 'var(--tint-error-fg)', fg: 'var(--tint-error-fg)', sub: 'Envios pausados',
  },
  // @v8 — PREJUÍZO CONSUMADO, não risco. `depleted` é a pílula CLARA (tint de erro): "pode dar
  // ruim". `losing` é a pílula CHEIA na cor de erro: já deu. A diferença é de superfície, não de
  // matiz — de relance os dois não se confundem, que é o único jeito de o segundo ter razão de
  // existir. Texto/ícone em --surface sobre --color-error: 5,71:1 no claro e 6,4:1 no escuro
  // (mesmo par que a badge de saldo esgotado já usa no mobile).
  losing: {
    border: 'var(--color-error)', bg: 'var(--color-error)',
    icon: ON_STATE_FILL, fg: ON_STATE_FILL, sub: 'Clientes sem aviso',
  },
  loading: {
    border: 'var(--divider)', bg: 'var(--bg-page)',
    icon: 'var(--text-secondary)', fg: 'var(--text-secondary)', sub: null,
  },
  disabled: {
    border: 'var(--divider)', bg: 'transparent',
    icon: 'var(--slate-400)', fg: 'var(--text-secondary)', sub: 'Ativar',
  },
};

/* ── Convite para ativar (layout.vm @v7: whatsapp.promoLabel) ──────────────────
 * Com a integração desligada, o chip "acorda" de tempos em tempos: 6 s depois de abrir o painel e
 * a cada 60 s, por 4,8 s, ele se expande em verde com o convite, um brilho atravessa a pílula e um
 * halo pulsa em volta. Passar o mouse (ou focar pelo teclado) mostra o convite parado, sem brilho.
 * Não roda com a aba em segundo plano nem com "reduzir movimento" ligado no sistema.
 * Texto verde-escuro sobre o verde oficial (--wa-on-main sobre --wa-main = 7,31:1): branco ali não
 * passaria de 3,09:1.
 */
const WA_PROMO = { firstDelayMs: 6000, intervalMs: 60000, showMs: 4800 };

/* ── Alarme de perda (layout.vm @v8: whatsapp.alertLabel) ─────────────────────
 * Mesmo mecanismo do convite, cadência própria e justificada:
 *   · abre em 3 s (contra 6 s) — o estrago JÁ aconteceu; fazer esperar o dobro do convite é
 *     tratar perda de cliente como propaganda;
 *   · repete a cada 45 s (contra 60 s) — mais insistente, mas ainda longe da frequência que
 *     vira papel de parede e para de ser vista;
 *   · fica 5,6 s (contra 4,8 s) — o alertLabel é bem mais longo que o convite e precisa caber
 *     numa leitura só.
 * A dramaturgia é outra: o convite BRILHA (varredura clara atravessando a pílula, tom de
 * promoção); o alarme NÃO brilha — ele bate, com um halo de dois toques em --color-error. */
const WA_ALERT = { firstDelayMs: 3000, intervalMs: 45000, showMs: 5600 };

function useWhatsAppPromoPulse(enabled, cfg = WA_PROMO) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    let hideTimer;
    const show = () => {
      if (document.hidden) return;
      setOn(true);
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => setOn(false), cfg.showMs);
    };
    const first = setTimeout(show, cfg.firstDelayMs);
    const loop = setInterval(show, cfg.intervalMs);
    return () => { clearTimeout(first); clearInterval(loop); clearTimeout(hideTimer); setOn(false); };
  }, [enabled, cfg]);
  return on;
}

const WA_PROMO_KEYFRAMES = {
  '@keyframes waPromoShine': {
    from: { transform: 'translateX(-130%) skewX(-18deg)' },
    to: { transform: 'translateX(330%) skewX(-18deg)' },
  },
  '@keyframes waPromoHalo': {
    '0%': { boxShadow: '0 0 0 0 color-mix(in srgb, var(--wa-main) 55%, transparent)' },
    '70%': { boxShadow: '0 0 0 12px color-mix(in srgb, var(--wa-main) 0%, transparent)' },
    '100%': { boxShadow: '0 0 0 0 color-mix(in srgb, var(--wa-main) 0%, transparent)' },
  },
  // halo de ALARME: dois toques seguidos e uma pausa (batida), na tinta de erro. O halo do
  // convite é uma onda só, lenta e simpática; esta cadência é a diferença entre "tem novidade"
  // e "tem problema", sem precisar de mais cor nem de mais texto.
  '@keyframes waAlertHalo': {
    '0%': { boxShadow: '0 0 0 0 color-mix(in srgb, var(--color-error) 60%, transparent)' },
    '16%': { boxShadow: '0 0 0 9px color-mix(in srgb, var(--color-error) 0%, transparent)' },
    '20%': { boxShadow: '0 0 0 0 color-mix(in srgb, var(--color-error) 60%, transparent)' },
    '38%': { boxShadow: '0 0 0 9px color-mix(in srgb, var(--color-error) 0%, transparent)' },
    '100%': { boxShadow: '0 0 0 0 color-mix(in srgb, var(--color-error) 0%, transparent)' },
  },
};

function HeaderWhatsAppChip({ wa, onOpen }) {
  const visible = wa?.visible !== false;
  const hasPromo = Boolean(visible && wa?.promoLabel);
  // @v8: o alarme usa o MESMO mecanismo de expansão do convite, com cadência própria.
  const hasAlert = Boolean(visible && wa?.status === 'losing' && wa?.alertLabel);
  const promoPulse = useWhatsAppPromoPulse(hasPromo, WA_PROMO);
  const alertPulse = useWhatsAppPromoPulse(hasAlert, WA_ALERT);
  const pulse = hasAlert ? alertPulse : promoPulse;
  const [peek, setPeek] = useState(false); // hover/foco mostram o texto parado, sem animação
  if (!wa || wa.visible === false) return null;
  const s = WA_STYLES[wa.status] || WA_STYLES.ok;
  const wide = hasPromo || hasAlert;                 // este estado tem texto para expandir
  const wideLabel = hasAlert ? wa.alertLabel : wa.promoLabel;
  const expanded = wide && (pulse || peek);
  // o convite VIRA verde ao expandir (a pílula está apagada em repouso); o alarme já nasce
  // vermelho cheio — expandir não troca a cor, só abre espaço para o texto.
  const dressUp = expanded && hasPromo;
  const longLabel = hasAlert ? `${wa.tooltip}. ${wa.alertLabel}` : hasPromo ? `${wa.tooltip}. ${wa.promoLabel}` : wa.tooltip;
  const focusRing = {
    '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '-2px' },
  };

  return (
    <>
      {/* desktop / tablet largo: pill com saldo — ou, desligado, o convite que se expande */}
      <Tooltip title={wa.tooltip}>
        <Stack direction="row" spacing={.75} alignItems="center"
          role="button" tabIndex={0} aria-label={longLabel}
          onClick={onOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); }
          }}
          onMouseEnter={() => setPeek(true)} onMouseLeave={() => setPeek(false)}
          onFocus={() => setPeek(true)} onBlur={() => setPeek(false)}
          sx={{
            ...WA_PROMO_KEYFRAMES,
            display: { xs: 'none', sm: 'flex' },
            position: 'relative', overflow: 'hidden',
            height: 40, px: 1.25, cursor: 'pointer', flexShrink: 0,
            borderRadius: 999, border: `1px solid ${dressUp ? 'var(--wa-main)' : s.border}`,
            bgcolor: dressUp ? 'var(--wa-main)' : s.bg,
            color: dressUp ? 'var(--wa-on-main)' : s.fg,
            transition: 'background-color .35s ease, border-color .35s ease, color .35s ease',
            animation: pulse && hasAlert ? 'waAlertHalo 1.9s ease-out infinite'
              : pulse && hasPromo ? 'waPromoHalo 1.6s ease-out infinite' : 'none',
            '&:hover': expanded || hasAlert ? {} : { bgcolor: 'var(--color-primary-soft)', borderColor: 'var(--color-primary-light)' },
            // o brilho que atravessa a pílula — SÓ no convite: varredura clara em cima de um
            // alarme de perda leria como propaganda em cima de prejuízo.
            '&::after': {
              content: '""', position: 'absolute', top: 0, bottom: 0, left: 0, width: '38%',
              pointerEvents: 'none',
              background: 'linear-gradient(90deg, transparent, color-mix(in srgb, var(--surface) 65%, transparent), transparent)',
              opacity: pulse && hasPromo ? 1 : 0,
              animation: pulse && hasPromo ? 'waPromoShine 1.9s ease-in-out infinite' : 'none',
            },
            ...focusRing,
          }}>
          <WhatsApp aria-hidden sx={{ fontSize: 20, color: dressUp ? 'var(--wa-on-main)' : s.icon, transition: 'color .35s ease' }} />
          {wa.status === 'loading' && (
            /* mesmo footprint de um saldo curto → sem layout shift */
            <Box sx={{ width: 28, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={14} thickness={5} sx={{ color: 'var(--text-secondary)' }} />
            </Box>
          )}
          {/* @v8: em `losing` o saldo vem null ("0" não diz nada) e o lugar do número é do
              PREJUÍZO. O sinal negativo é a mensagem inteira — não é "você tem 0", é "você já
              perdeu 3" — então ele vem um degrau acima do saldo na escala (subtitle1/700).
              `tabular-nums` trava a largura: -1 e -3 não podem mudar o tamanho da pílula. */}
          {wa.status === 'losing' && wa.lostLabel && (
            <Typography component="span" variant="subtitle1"
              sx={{ fontWeight: 700, color: 'inherit', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {wa.lostLabel}
            </Typography>
          )}
          {wa.creditsLabel != null && (
            <Typography component="span" variant="body2"
            sx={{ fontWeight: 700, color: s.fg, lineHeight: 1 }}>
              {wa.creditsLabel}
            </Typography>
          )}
          {s.sub && (
            <Typography component="span" variant="caption"
              sx={{
                color: 'inherit', lineHeight: 1, whiteSpace: 'nowrap',
                // no md+ o "Ativar" dá lugar ao convite; no sm (header apertado) ele fica
                display: expanded ? { xs: 'block', md: 'none' } : 'block',
                fontWeight: expanded ? 700 : 400,
              }}>
              {s.sub}
            </Typography>
          )}
          {wide && (
            <Typography component="span" variant="body2" aria-hidden
              sx={{
                display: { xs: 'none', md: 'block' },
                // o alarme é bem mais comprido que o convite; onde não couber, corta com
                // reticências (o texto inteiro está no tooltip e no aria-label).
                maxWidth: expanded ? (hasAlert ? { md: 360, lg: 520 } : 320) : 0,
                opacity: expanded ? 1 : 0,
                overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                fontWeight: 700, lineHeight: 1,
                color: 'inherit',
                ml: expanded ? undefined : '0 !important',
                transition: 'max-width .55s cubic-bezier(.22,1,.36,1), opacity .3s ease',
              }}>
              {wideLabel}
            </Typography>
          )}
        </Stack>
      </Tooltip>

      {/* mobile: só ícone + badge. Não existe pílula para expandir, então o alarme vira PULSO:
          durante o ciclo o ícone bate em --color-error com o mesmo halo de dois toques. O texto
          do alarme continua acessível pelo tooltip e pelo aria-label — "no celular não alarma"
          não serve: é onde o dono mais está. */}
      <Tooltip title={hasAlert ? wa.alertLabel : hasPromo ? wa.promoLabel : wa.tooltip}>
        <IconButton aria-label={longLabel} onClick={onOpen}
          sx={{
            ...WA_PROMO_KEYFRAMES,
            display: { xs: 'inline-flex', sm: 'none' },
            width: 44, height: 44, borderRadius: 'var(--radius-sm)',
            // ATENÇÃO: em `losing` o s.icon é --surface (ícone sobre a pílula cheia do desktop).
            // Aqui não há pílula atrás, então o ícone tem que voltar para a cor de erro, senão
            // some no fundo da barra.
            color: wa.status === 'losing' ? 'var(--color-error)'
              : pulse && hasPromo ? 'var(--wa-main)' : s.icon,
            animation: pulse && hasAlert ? 'waAlertHalo 1.9s ease-out infinite'
              : pulse && hasPromo ? 'waPromoHalo 1.6s ease-out infinite' : 'none',
            transition: 'color .35s ease',
            '&:hover': { bgcolor: 'var(--color-primary-soft)' },
            ...focusRing,
          }}>
          <Badge
            overlap="circular"
            badgeContent={wa.status === 'losing' ? (wa.lostLabel ?? undefined) : (wa.creditsLabel ?? undefined)}
            invisible={wa.status === 'losing' ? wa.lostLabel == null : wa.creditsLabel == null}
            sx={{
              '& .MuiBadge-badge': {
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                bgcolor: (wa.status === 'depleted' || wa.status === 'losing') ? 'var(--color-error)'
                  : wa.status === 'low' ? 'var(--color-warning)' : 'var(--tint-neutral-bg)',
                color: (wa.status === 'depleted' || wa.status === 'losing' || wa.status === 'low') ? ON_STATE_FILL : 'var(--tint-neutral-fg)',
                border: wa.status === 'ok' ? '1px solid var(--tint-neutral-border)' : 'none',
                // a badge respira junto com o pulso do ícone: o -3 é o dado, e é ele que precisa
                // ser visto quando o alarme dispara.
                '@keyframes waAlertBreath': {
                  '0%, 100%': { transform: 'scale(1) translate(50%, -50%)' },
                  '50%': { transform: 'scale(1.18) translate(42%, -42%)' },
                },
                animation: pulse && hasAlert ? 'waAlertBreath 1.9s ease-in-out infinite' : 'none',
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              },
            }}>
            <WhatsApp sx={{ fontSize: 22 }} />
          </Badge>
        </IconButton>
      </Tooltip>
    </>
  );
}

/* conteúdo do drawer extraído p/ reuso nos dois variants (mobile/desktop) */
function DrawerContent({ nav, activeRoute, actions, onNavigateDone }) {
  return (
    <>
        <Toolbar />
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography variant="caption" sx={{ color: DRAWER_TEXT_MUTED, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>
            Menu
          </Typography>
        </Box>
        <List sx={{ px: 1, py: 0, flex: 1, overflowY: 'auto' }}>
          {nav.map((item) => {
            const Icon = ICONS[item.iconName] || HelpOutline;
            const selected = item.route === activeRoute;
            const soon = !item.migrated;
            return (
              <ListItemButton
                key={item.key}
                onClick={() => { actions?.onNavigate?.(item.route); onNavigateDone?.(); }}
                aria-current={selected ? 'page' : undefined}
                sx={{
                  position: 'relative', mb: .25, minHeight: 44, borderRadius: 'var(--radius-sm)',
                  color: selected ? DRAWER_ACCENT : DRAWER_TEXT,
                  bgcolor: selected ? 'var(--color-primary-soft)' : 'transparent',
                  opacity: soon && !selected ? 0.62 : 1,
                  transition: 'background-color .15s, color .15s',
                  '&:hover': { bgcolor: 'var(--color-primary-soft)' },
                  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '-2px' },
                  // barra de acento à esquerda quando ativo
                  '&::before': selected ? {
                    content: '""', position: 'absolute', left: 0, top: 8, bottom: 8, width: 3,
                    borderRadius: 3, bgcolor: DRAWER_ACCENT,
                  } : {},
                }}
              >
                <ListItemIcon sx={{ minWidth: 38, color: selected ? DRAWER_ACCENT : DRAWER_TEXT_MUTED }}>
                  <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: 14, fontWeight: selected ? 700 : 500 }}
                />
                {soon && (
                  <Box component="span" sx={{
                    ml: 1, px: .75, py: '2px', borderRadius: 999, flexShrink: 0,
                    border: `1px solid ${DRAWER_LINE}`, color: DRAWER_TEXT_MUTED,
                    fontSize: 10, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase',
                    lineHeight: 1.4, bgcolor: 'transparent',
                  }}>
                    em breve
                  </Box>
                )}
              </ListItemButton>
            );
          })}
        </List>

        <Box sx={{ p: 2, borderTop: `1px solid ${DRAWER_LINE}` }}>
          <Stack direction="row" spacing={1} alignItems="center"
            role="button" tabIndex={0} aria-label="Abrir central de ajuda"
            onClick={() => { actions?.onOpenSupportWidget?.(); onNavigateDone?.(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); actions?.onOpenSupportWidget?.(); onNavigateDone?.(); }
            }}
            sx={{
              color: DRAWER_TEXT_MUTED, cursor: 'pointer', borderRadius: 'var(--radius-sm)',
              mx: -.75, px: .75, py: .5,
              '&:hover': { bgcolor: 'var(--color-primary-soft)', color: DRAWER_TEXT },
              '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '-2px' },
            }}>
            <HelpOutline sx={{ fontSize: 18 }} />
            <Typography variant="caption">Central de ajuda</Typography>
          </Stack>
        </Box>
    </>
  );
}
